import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    Badge,
    Flex,
    HorizontalDivider,
    IconButton,
    IconChevronRight,
    IconInfo,
    Stack,
    Subtitle1,
    TextButton,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { ConditionalComponent } from '../../utility/ConditionalComponent';

import { MedicineCabinetInfoModal } from './MedicineCabinetInfoModal';

export type MedicineCabinetCardHeaderProps = {
    count: number;
    showButton?: boolean;
};

export const MedicineCabinetCardHeader = ({
    count,
    showButton = true,
}: MedicineCabinetCardHeaderProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [opened, { toggle }] = useDisclosure(false);

    const navigateToCabinetRoot = useCallback(() => {
        navigate(getAbsoluteRoute('cabinet.root'));
    }, [navigate]);

    return (
        <Stack gap="xs">
            <Flex justify="space-between">
                <Flex gap="xxs">
                    <ConditionalComponent condition={count > 0}>
                        <Subtitle1 color="currentColor">
                            {t('cabinet.ordersCard.header')}
                        </Subtitle1>

                        <IconButton
                            Icon={IconInfo}
                            color="primary"
                            onClick={toggle}
                            withStyles={false}
                        />

                        <MedicineCabinetInfoModal
                            close={toggle}
                            isOpen={opened}
                        />
                    </ConditionalComponent>

                    <ConditionalComponent condition={count === 0}>
                        {t('cabinet.noOrdersCard.header')}
                    </ConditionalComponent>
                </Flex>

                <ConditionalComponent condition={showButton}>
                    {count > 3 ? (
                        <TextButton onClick={navigateToCabinetRoot}>
                            <Flex gap="xxs" align="center">
                                {t('cabinet.ordersCard.buttons.seeAll')}
                                <Badge radius="lg" p="xxxs">
                                    {count < 10 ? `${count}` : '9+'}
                                </Badge>
                            </Flex>
                        </TextButton>
                    ) : (
                        <IconButton
                            withStyles={false}
                            Icon={IconChevronRight}
                            onClick={navigateToCabinetRoot}
                            alt={t('cabinet.ordersCard.buttons.seeAll')}
                        />
                    )}
                </ConditionalComponent>
            </Flex>

            <ConditionalComponent condition={count > 0}>
                <HorizontalDivider />
            </ConditionalComponent>
        </Stack>
    );
};
